import React, { useEffect, useRef, useState } from 'react';
import { DIRECTORY_FACTS, FACT_ICONS, Fact } from './factLoaderData';

const ROTATE_MS = 3000; // time each fact stays on screen
const FADE_MS = 400; // must match the .uv-fact-text/.uv-fact-icon CSS transition
const PICK_COUNT = 4; // how many facts to show per load — 4 x 3s = 12s of rotation

interface FactLoaderProps {
    show: boolean;
    facts?: Fact[];
}

const shuffle = <T,>(arr: T[]): T[] => [...arr].sort(() => Math.random() - 0.5);

/**
 * Full-screen loader shown while directory searches are in flight — a
 * branded logo animation plus a rotating pool of facts/quips (in place of a
 * bare spinner) so long-running searches don't feel stalled.
 */
const FactLoader = ({ show, facts = DIRECTORY_FACTS }: FactLoaderProps) => {
    const [pool, setPool] = useState<Fact[]>(facts);
    const [index, setIndex] = useState(0);
    const [fading, setFading] = useState(false);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!show) return;

        // Pick a fresh random subset each time the loader appears (new page load,
        // new search, etc.) rather than cycling every fact in the pool.
        const selected = shuffle(facts).slice(0, PICK_COUNT);
        setPool(selected);
        setIndex(0);
        setFading(false);

        timerRef.current = setInterval(() => {
            setFading(true);
            fadeTimeoutRef.current = setTimeout(() => {
                setIndex((prev) => (prev + 1) % selected.length);
                setFading(false);
            }, FADE_MS);
        }, ROTATE_MS);

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    if (!show) return null;

    const current = pool[index] ?? facts[0];
    const dotCount = Math.min(pool.length, 6);

    return (
        <div className="uv-loader-overlay bg-black bg-opacity-50 d-flex align-items-center justify-content-center" role="status" aria-live="polite">
            <div className="uv-loader-card bg-white rounded-3 p-5 text-center">
                <div className="uv-logo-loader" aria-hidden="true">
                    {/* Official Univariety mark. Stripes animate top-to-bottom (purple -> blue -> red -> orange -> amber) via --i. */}
                    <svg viewBox="0 0 231.776 305.924" width="56" height="74" xmlns="http://www.w3.org/2000/svg">
                        <g transform="translate(-1232.412 535.962)">
                            <g transform="translate(1232.412 -535.962)"><path className="uv-logo-stripe" style={{ '--i': 0 } as React.CSSProperties} fill="#51499c" d="M0,30.592H0V29.555C0,24.37,1.037,18.667,4.148,14A26.849,26.849,0,0,1,28,0,33.015,33.015,0,0,1,42,3.63a26.81,26.81,0,0,1,13.481,19.7V53.926L0,86.592Z" /></g>
                            <g transform="translate(1232.412 -510.014)"><path className="uv-logo-stripe" style={{ '--i': 1 } as React.CSSProperties} fill="#26a7df" d="M.519,136.347c0-8.3-.519-17.111-.519-25.926V72.051L106.3,10.867a26.938,26.938,0,0,0,2.593-1.556h.519a32.913,32.913,0,0,1,14-3.111,17.64,17.64,0,0,1,5.185.519h1.037c12.963,2.593,21.778,14,21.778,27.481a27.453,27.453,0,0,1-14,23.852l-1.037.519-16.592,8.815Z" /></g>
                            <g transform="translate(1233.249 -477.788)"><path className="uv-logo-stripe" style={{ '--i': 2 } as React.CSSProperties} fill="#d42941" d="M9.015,175.158l-1.556-4.667C3.83,159.084,1.237,140.418.2,115.529l170.073-98h.519a7.99,7.99,0,0,1,3.63-1.556A32.011,32.011,0,0,1,185.828,13.9h3.111a4.674,4.674,0,0,1,2.593.519c1.037,0,1.556.519,2.593.519h0c.519,0,1.556.519,2.074.519s.519.519,1.037.519l.519.519A28.043,28.043,0,0,1,213.828,41.9,28.745,28.745,0,0,1,201.9,64.714L168.2,83.9Z" /></g>
                            <g transform="translate(1243.293 -423.8)"><path className="uv-logo-stripe" style={{ '--i': 3 } as React.CSSProperties} fill="#f05a2a" d="M38.378,174.058c-.519-.518-1.037-.518-1.556-1.037C21.785,162.132,10.378,147.614,2.6,129.984L175.265,30.43a1.81,1.81,0,0,1,1.037-.519h.519A26.347,26.347,0,0,1,189.265,26.8h1.556a30.319,30.319,0,0,1,9.852,2.074,27.662,27.662,0,0,1,16.592,25.407A28.414,28.414,0,0,1,202.228,79.17L38.378,174.058Z" /></g>
                            <g transform="translate(1279.704 -364.371)"><path className="uv-logo-stripe" style={{ '--i': 4 } as React.CSSProperties} fill="#f8b018" d="M72.485,134.333c-23.852,0-44.592-4.667-61.185-13.481l130.666-75.7a57.063,57.063,0,0,1,6.222-3.111,22.052,22.052,0,0,1,5.7-1.037H157a35.012,35.012,0,0,1,7.259,1.037,27.722,27.722,0,0,1,20.222,23.852V69a28.134,28.134,0,0,1-7.259,18.667,77.414,77.414,0,0,1-7.259,6.222h-.519c-.519.519-.519.519-1.037.519l-42,23.852a36.385,36.385,0,0,0-5.7,3.63h0C106.707,129.666,90.114,133.814,72.485,134.333Z" /></g>
                        </g>
                    </svg>
                </div>

                <div className={`uv-fact-icon${fading ? ' is-fading' : ''}`} aria-hidden="true">
                    {FACT_ICONS[current.icon]}
                </div>

                <div className="uv-fact d-flex align-items-center justify-content-center">
                    <div className={`uv-fact-text${fading ? ' is-fading' : ''}`}>{current.text}</div>
                </div>

                <div className="uv-fact-dots mt-3 d-flex gap-2 justify-content-center" aria-hidden="true">
                    {Array.from({ length: dotCount }, (_, i) => (
                        <span key={i} className={i === index % dotCount ? 'is-active' : ''} />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default FactLoader;
